import { Check, Sparkles, ArrowUpRight } from "lucide-react";

const MAIN_TIERS = [
  {
    name: "Full Membership",
    annual: "£750.00",
    monthly: "£65.00 / month",
    benefit: "Play golf on 364 days of the year",
    accent: false,
  },
  {
    name: "Full Joint Membership",
    annual: "£1300.00",
    monthly: "£110.00 / month",
    benefit: "Ideal family option for unlimited golf in Swansea",
    accent: true,
  },
  {
    name: "Country Membership",
    annual: "£450.00",
    monthly: "£40.00 / month",
    benefit: "Restrictions apply — ask the office for tee window details",
    accent: false,
  },
];

const YOUTH = [
  { name: "Under 35 (U35)", annual: "£650.00", note: "£150.00 upfront + £40.00 / month" },
  { name: "Under 30 (U30)", annual: "£560.00", note: "£150.00 upfront + £35.00 / month" },
  { name: "Student (Full Time)", annual: "£360.00", note: "Free with full member parent" },
  { name: "Under 18 (U18)", annual: "£120.00", note: "Junior membership" },
  { name: "Under 16 (U16)", annual: "£80.00", note: "Cadet membership" },
];

export default function Membership() {
  return (
    <section
      id="membership"
      data-testid="membership-section"
      className="relative bg-[#FAFAFA] py-24 md:py-32 lg:py-40 border-y border-[#CBA153]/15 overflow-hidden"
    >
      {/* Soft golf landscape backdrop */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src="https://images.pexels.com/photos/35320850/pexels-photo-35320850.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1800"
          alt=""
          className="w-full h-full object-cover opacity-[0.14]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAFAFA] via-[#FAFAFA]/85 to-[#FAFAFA]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto">
          <span className="section-eyebrow"> 2026–27 Season</span>
          <h2 className="font-serif-luxury text-4xl md:text-5xl lg:text-6xl text-[#0F4C3A] mt-5 leading-[1.05] tracking-tight">
            Memberships at
            <span className="block italic text-gold-metallic">The Gower Golf Club</span>
          </h2>
          <p className="text-[#4A4A4A] mt-6 leading-relaxed">
            Transparent pricing for the 2026-27 season. Become part of Swansea's most welcoming
            golf community.
          </p>

          <a
            data-testid="buy-membership-cta-top"
            href="https://memberships-101273.square.site/s/shop"
            target="_blank"
            rel="noopener noreferrer"
            className="ggc-btn-solid-gold mt-10 inline-flex"
          >
            Buy or Renew Membership
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
        </div>

        {/* Main tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7 mt-16">
          {MAIN_TIERS.map((t, i) => (
            <article
              key={t.name}
              data-testid={`membership-tier-${i}`}
              className={`relative bg-white border p-9 hover-lift transition-all ${
                t.accent ? "border-[#CBA153] shadow-[0_20px_50px_rgba(203,161,83,0.12)]" : "border-[#CBA153]/30"
              }`}
            >
              {t.accent && (
                <div className="absolute -top-3 left-9 bg-[#CBA153] text-white text-[10px] tracking-[0.22em] uppercase px-3 py-1 flex items-center gap-1.5">
                  <Sparkles size={10} strokeWidth={1.5} /> Most Loved
                </div>
              )}
              <p className="section-eyebrow">Annual</p>
              <h3 className="font-serif-luxury text-2xl text-[#0F4C3A] mt-2">{t.name}</h3>
              <div className="mt-6">
                <p className="font-serif-luxury text-5xl text-[#0F4C3A] tracking-tight">{t.annual}</p>
                <p className="text-sm text-[#4A4A4A] mt-2">{t.monthly}</p>
              </div>
              <div className="my-6 h-px bg-[#CBA153]/40" />
              <div className="flex items-start gap-3 text-sm text-[#4A4A4A] leading-relaxed">
                <Check size={16} strokeWidth={1.5} className="text-[#0F4C3A] mt-0.5 shrink-0" />
                <span>{t.benefit}</span>
              </div>
            </article>
          ))}
        </div>

        {/* Youth concessions */}
        <div className="mt-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <span className="section-eyebrow">Age-Related Concessions &amp; Youth Tiers</span>
              <h3 className="font-serif-luxury text-3xl md:text-4xl text-[#0F4C3A] mt-3 leading-tight">
                For every generation
                <span className="italic text-gold-metallic"> of Gower golfer.</span>
              </h3>
            </div>
            <div className="h-px bg-[#CBA153]/40 flex-1 hidden md:block ml-10" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {YOUTH.map((y, i) => (
              <article
                key={y.name}
                data-testid={`youth-tier-${i}`}
                className="bg-white border border-[#CBA153]/30 p-6 hover-lift"
              >
                <p className="text-[10px] tracking-[0.22em] uppercase text-gold-metallic">{y.name}</p>
                <p className="font-serif-luxury text-3xl text-[#0F4C3A] mt-3">{y.annual}</p>
                <div className="h-px bg-[#CBA153]/30 my-4" />
                <p className="text-xs text-[#4A4A4A] leading-relaxed">{y.note}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-16 text-center">
          <a
            data-testid="buy-membership-cta-bottom"
            href="https://memberships-101273.square.site/s/shop"
            target="_blank"
            rel="noopener noreferrer"
            className="ggc-btn-solid-gold inline-flex"
          >
            Buy or Renew Membership
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
          <p className="text-xs text-[#4A4A4A] mt-4 tracking-wider">
            Secure checkout via our official membership portal.
          </p>
        </div>
      </div>
    </section>
  );
}
